import { Injectable } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  private readonly isClearBS: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true);

  public header_title: BehaviorSubject<string> = new BehaviorSubject<string>(
    'Login'
  );

  private readonly titleSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly titleService: Title
  ) {}

  getIsClear(): BehaviorSubject<boolean> {
    return this.isClearBS;
  }

  setIsClear(value: boolean): void {
    this.isClearBS.next(value);
  }

  static readonly DEFAULT_IMAGE =
    'assets/images/default_images/team_categories/Media Obiettivo.png';
  private readonly urlCache = new Map<string, any>();
  private readonly failedUrls = new Set<string>();

  getSanitizeUrl(url: string) {
    if (!url) {
      return UtilityService.DEFAULT_IMAGE;
    }
    if (typeof url !== 'string') {
      return url; // already a SafeUrl from a previous getSanitizeUrl call, return as-is
    }
    if (url.includes('assets')) {
      return url;
    }

    if (this.failedUrls.has(url)) {
      return UtilityService.DEFAULT_IMAGE;
    }

    const cached = this.urlCache.get(url);
    if (cached) {
      return cached;
    }

    // If the backend returned a full absolute URL (e.g. https://storage.internal:9000/...),
    // extract just the pathname so we can rebase it on environment.file.
    if (url.startsWith('http://') || url.startsWith('https://')) {
      try {
        url = new URL(url).pathname.replace(/^\//, '');
      } catch (e) {
        console.warn('getSanitizeUrl: invalid URL', url, e);
      }
    }

    let path = environment.file + url;

    if (path.includes('#')) {
      let sharp = encodeURIComponent('#');
      path =
        path.slice(0, path.indexOf('#')) +
        sharp +
        path.slice(path.indexOf('#') + 1);
    } else if (path.includes('++')) {
      let sharp = encodeURIComponent('++');
      path =
        path.slice(0, path.indexOf('++')) +
        sharp +
        path.slice(path.indexOf('++') + 2);
    }

    const result = this.sanitizer.bypassSecurityTrustUrl(path);
    this.urlCache.set(url, result);
    return result;
  }

  markImageAsFailed(url: string): void {
    this.failedUrls.add(url);
    this.urlCache.delete(url);
  }

  updateTitle(newTitle: string) {
    this.titleService.setTitle(`${newTitle}`);
  }

  setTitle(newTitle: string) {
    this.titleSubject.next(newTitle);
  }

  getTitle() {
    return this.titleSubject.asObservable();
  }

  disabledDate = (date: Date): boolean => {
    if (!date) {
      return false;
    }

    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return true;
    }

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    const fixedHolidays = [
      { month: 1, day: 1 },
      { month: 1, day: 6 },
      { month: 2, day: 5 },
      { month: 4, day: 25 },
      { month: 5, day: 1 },
      { month: 6, day: 2 },
      { month: 8, day: 15 },
      { month: 11, day: 1 },
      { month: 12, day: 8 },
      { month: 12, day: 25 },
      { month: 12, day: 26 }
    ];

    if (
      fixedHolidays.some(
        holiday => holiday.month === month && holiday.day === day
      )
    ) {
      return true;
    }

    const easter = this.calculateEaster(year);
    const easterMonday = new Date(easter);
    easterMonday.setDate(easterMonday.getDate() + 1);

    return (
      (month === easter.getMonth() + 1 && day === easter.getDate()) ||
      (month === easterMonday.getMonth() + 1 && day === easterMonday.getDate())
    );
  };

  private calculateEaster(year: number): Date {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }
}
